import React, { useState, useEffect, useCallback } from "react";
import {
  Search,
  Moon,
  Sun,
  Monitor,
  User,
  Settings,
  LogOut,
  UserPlus,
  AlertCircle,
  X,
  Type,
  Palette,
  Zap,
  Eye,
  Volume2,
  Menu,
  RefreshCw,
  ChevronRight,
  Home,
  Languages,
  Brain,
  Coins,
} from "lucide-react";
import { useTheme } from "../contexts/ThemeContext";
import { useAuth } from "../contexts/AuthContext";
import { useToken } from "../contexts/TokenContext";
import { UserManagement } from "./UserManagement";
import { AdminProfile } from "./admin/AdminProfile";
import { useNavigation } from "../contexts/NavigationContext";
import { useBreadcrumbs } from "../contexts/BreadcrumbContext";
import { useTranslation } from "../i18n/hooks/useTranslation";
import { SUPPORTED_LANGUAGES } from "../i18n/config";
import { GlobalSearch } from "./GlobalSearch";
import { AIArchitectPanel } from "./dev/AIArchitectPanel";

interface HeaderProps {
  onMobileMenuToggle: () => void;
}

interface UserData {
  _id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  profileImage?: string;
}

export const Header: React.FC<HeaderProps> = ({ onMobileMenuToggle }) => {
  const { theme, setTheme, accessibility, setAccessibility } = useTheme();
  const { user, logout, stopImpersonation, impersonate } = useAuth();
  const { balance, freeMonthlyAllowance, freeTokensRemaining, totalPurchased, isLoading: tokenLoading, openPurchaseModal } = useToken();
  const { addNavigationItem, navigationItems, navigate, currentPath } =
    useNavigation();
  const { customBreadcrumbs } = useBreadcrumbs();
  const { t, language, changeLanguage } = useTranslation();
  const [showThemeMenu, setShowThemeMenu] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [showUserSwitcher, setShowUserSwitcher] = useState(false);
  const [showSystemAdminSwitcher, setShowSystemAdminSwitcher] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [availableUsers, setAvailableUsers] = useState<UserData[]>([]);
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [showArchitectPanel, setShowArchitectPanel] = useState(false);
  const [architectTrigger, setArchitectTrigger] = useState<any>(null);
  const [showTokenMenu, setShowTokenMenu] = useState(false);
  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";

  const isSystemAdmin = user?.role === "System Administrator";
  const isDevelopment = process.env.NODE_ENV === "development";

  // balance is the current available tokens (total)
  // freeTokensRemaining is the remaining free tokens this month
  // totalPurchased is lifetime purchased tokens (cumulative)
  const totalTokens = balance;
  const freeTokens = freeTokensRemaining; // Use actual remaining free tokens
  // Purchased tokens currently in balance (not lifetime total)
  // This is the portion of balance that is NOT from free allowance
  const purchasedTokensInBalance = Math.max(0, balance - freeTokensRemaining);
  const isLowBalance = totalTokens < 100 && totalTokens > 0;

  // Listen for global search queries to send to AI Architect Panel
  useEffect(() => {
    const handleGlobalSearch = (
      event: CustomEvent<{ message: string; language?: string }>,
    ) => {
      if (showArchitectPanel && isDevelopment) {
        // Create a new object with timestamp to ensure useEffect triggers
        setArchitectTrigger({
          message: event.detail.message,
          language: event.detail.language || "en",
          timestamp: Date.now(), // Add timestamp to ensure object reference changes
        });
      }
    };

    window.addEventListener("globalSearchTrigger" as any, handleGlobalSearch);
    return () => {
      window.removeEventListener(
        "globalSearchTrigger" as any,
        handleGlobalSearch,
      );
    };
  }, [showArchitectPanel, isDevelopment]);

  // Helper function to translate path segments to navigation keys
  const translatePathSegment = (segment: string): string => {
    const pathMap: Record<string, string> = {
      dashboard: t("navigation.dashboard"),
      quotes: t("navigation.quotes"),
      leads: t("navigation.leads"),
      active: t("navigation.activequotes"),
      accepted: t("navigation.acceptedquotes"),
      jobs: t("navigation.jobs"),
      projects: t("navigation.projects"),
      staff: t("navigation.staff"),
      contacts: t("navigation.contacts"),
      timesheets: t("navigation.timesheets"),
      schedules: t("navigation.calendar"),
      admin: t("navigation.admin"),
      settings: t("navigation.settings"),
      profile: t("navigation.profile"),
      account: t("navigation.account"),
    };
    return (
      pathMap[segment.toLowerCase()] ||
      segment
        .split("-")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ")
    );
  };

  // Generate breadcrumbs from current path
  const getBreadcrumbs = () => {
    // If custom breadcrumbs are set, use them (they should already be translated by the component setting them)
    if (customBreadcrumbs) {
      return customBreadcrumbs;
    }

    const breadcrumbs = [
      { label: t("navigation.home"), path: "/", icon: "Home" },
    ];

    // Remove query parameters from path for breadcrumb matching
    const pathWithoutQuery = currentPath.split("?")[0];

    // Find matching navigation item (using path without query params)
    const currentItem = navigationItems.find(
      (item) => item.path === pathWithoutQuery || item.path === currentPath,
    );

    if (currentItem && pathWithoutQuery !== "/") {
      // Translate the navigation item label
      const translatedLabel =
        t(`navigation.${currentItem.id.toLowerCase()}`) || currentItem.label;
      breadcrumbs.push({
        label: translatedLabel,
        path: currentItem.path || currentPath,
        icon: currentItem.icon,
      });
    } else if (currentPath !== "/") {
      // Fallback for paths not in navigation - translate path segments
      // Don't show query parameters in breadcrumb label
      const pathSegments = pathWithoutQuery.split("/").filter(Boolean);
      if (pathSegments.length > 0) {
        const translatedLabel = translatePathSegment(
          pathSegments[pathSegments.length - 1],
        );
        breadcrumbs.push({
          label: translatedLabel,
          path: currentPath,
          icon: "",
        });
      }
    }

    return breadcrumbs;
  };

  const handleThemeChange = (mode: "light" | "dark" | "system") => {
    setTheme({ ...theme, mode });
    setShowThemeMenu(false);
  };

  const handleViewModeChange = (viewMode: "standard" | "compact") => {
    setTheme({ ...theme, viewMode });
    setShowThemeMenu(false);
  };

  const handleAdminClick = () => {
    setShowUserMenu(false);
    // Add Admin to navigation if not already there
    addNavigationItem({
      id: "admin",
      label: t("navigation.admin"),
      icon: "Settings",
      path: "/admin",
    });
    // Navigate to admin
    navigate("/admin");
  };

  const handleProfileClick = () => {
    setShowUserMenu(false);
    setShowProfileModal(true);
  };

  const handleStopImpersonation = async () => {
    try {
      await stopImpersonation();
    } catch (error) {
      console.error("Failed to stop impersonation:", error);
    }
  };

  const fetchUsersForSwitching = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${apiUrl}/api/users`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (data.success) {
        // Filter out current impersonated user
        setAvailableUsers(
          data.data.filter((u: UserData) => u._id !== user?._id),
        );
      }
    } catch (error) {
      console.error("Failed to fetch users:", error);
    }
  }, [apiUrl, user?._id]);

  const handleSwitchUser = async (userId: string) => {
    try {
      await impersonate(userId);
      setShowUserSwitcher(false);
      setUserSearchTerm("");
    } catch (error) {
      console.error("Failed to switch user:", error);
    }
  };

  useEffect(() => {
    if (showUserSwitcher) {
      fetchUsersForSwitching();
    }
  }, [showUserSwitcher, fetchUsersForSwitching]);

  useEffect(() => {
    if (showSystemAdminSwitcher) {
      fetchUsersForSwitching();
    }
  }, [showSystemAdminSwitcher, fetchUsersForSwitching]);

  const filteredUsers = availableUsers.filter(
    (u) =>
      u.email.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
      u.firstName.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
      u.lastName.toLowerCase().includes(userSearchTerm.toLowerCase()),
  );

  // Helper function to get user initials
  const getUserInitials = (firstName?: string, lastName?: string): string => {
    const first = firstName?.charAt(0)?.toUpperCase() || "";
    const last = lastName?.charAt(0)?.toUpperCase() || "";
    return first + last || "U";
  };

  // Debug: Log user impersonation state
  console.log('[Header] User impersonation state:', {
    impersonating: user?.impersonating,
    email: user?.email,
    originalAdmin: (user as any)?.originalAdmin
  });

  return (
    <>
      {/* Impersonation Warning Banner */}
      {user?.impersonating && (
        <div className="fixed top-0 left-0 right-0 bg-warning-500 dark:bg-warning-600 text-white px-2 sm:px-4 h-[44px] flex items-center z-50">
          <div className="flex items-center justify-between max-w-7xl mx-auto w-full gap-1 sm:gap-2">
            <div className="flex items-center gap-1 sm:gap-2 min-w-0 flex-1">
              <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
              {/* Mobile: Show only name, Desktop: Show full details */}
              <span className="font-medium text-xs sm:text-sm truncate">
                <span className="sm:hidden">
                  {user.firstName} {user.lastName}
                </span>
                <span className="hidden sm:inline">
                  {t("header.impersonating")}: {user.firstName} {user.lastName}{" "}
                  ({user.email})
                </span>
              </span>
              {/* Hide admin info on mobile */}
              {user.originalAdmin && (
                <span className="hidden md:inline text-warning-100 text-sm flex-shrink-0">
                  | {t("header.admin")}: {user.originalAdmin.email} (
                  {user.originalAdmin.role})
                </span>
              )}
            </div>
            <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
              <button
                onClick={() => setShowUserSwitcher(!showUserSwitcher)}
                className="flex items-center gap-1 bg-warning-400 dark:bg-warning-500 text-warning-900 px-2 sm:px-3 py-1 rounded-md hover:bg-warning-300 dark:hover:bg-warning-400 transition-colors font-medium text-xs sm:text-sm"
                title={t("header.switchUserTooltip")}
              >
                <RefreshCw className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">
                  {t("header.switchUser")}
                </span>
              </button>
              <button
                onClick={handleStopImpersonation}
                className="flex items-center gap-1 bg-white text-warning-600 px-2 sm:px-3 py-1 rounded-md hover:bg-warning-50 transition-colors font-medium text-xs sm:text-sm"
                title={t("header.stopImpersonationTooltip")}
              >
                <X className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">
                  {t("header.stopImpersonation")}
                </span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* System Admin Banner - Global */}
      {isSystemAdmin && !user?.impersonating && (
        <div className="fixed top-0 left-0 right-0 bg-accent-600 dark:bg-accent-700 text-white px-4 h-[44px] flex items-center z-50">
          <div className="flex items-center justify-between max-w-7xl mx-auto w-full">
            <div className="flex items-center space-x-2">
              <Settings className="w-5 h-5" />
              <span className="font-medium text-lg">
                Viewing as System Admin
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() =>
                  setShowSystemAdminSwitcher(!showSystemAdminSwitcher)
                }
                className="flex items-center space-x-1 bg-accent-500 dark:bg-accent-600 text-white px-3 py-1 rounded-md hover:bg-accent-400 dark:hover:bg-accent-500 transition-colors font-medium text-sm"
              >
                <RefreshCw className="w-4 h-4" />
                <span>Switch to User View</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User Switcher Modal */}
      {showUserSwitcher && user?.impersonating && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-[60]"
            onClick={() => setShowUserSwitcher(false)}
          />
          <div className="fixed top-16 right-4 w-96 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-2xl z-[70] max-h-[80vh] flex flex-col">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Switch User
                </h3>
                <button
                  onClick={() => setShowUserSwitcher(false)}
                  className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search users..."
                  value={userSearchTerm}
                  onChange={(e) => setUserSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-2">
              {filteredUsers.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  {availableUsers.length === 0
                    ? "Loading users..."
                    : "No users found"}
                </div>
              ) : (
                <div className="space-y-1">
                  {filteredUsers.map((u) => (
                    <button
                      key={u._id}
                      onClick={() => handleSwitchUser(u._id)}
                      className="w-full text-left px-3 py-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
                          {u.profileImage ? (
                            <img
                              src={u.profileImage}
                              alt="Profile"
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <User className="w-4 h-4 text-white" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                            {u.firstName} {u.lastName}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {u.email}
                          </p>
                        </div>
                        <span className="text-xs text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                          {u.role}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* System Admin User Switcher Modal */}
      {showSystemAdminSwitcher && isSystemAdmin && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-[60]"
            onClick={() => setShowSystemAdminSwitcher(false)}
          />
          <div className="fixed top-16 right-4 w-96 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-2xl z-[70] max-h-[80vh] flex flex-col">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Switch to User View
                </h3>
                <button
                  onClick={() => setShowSystemAdminSwitcher(false)}
                  className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search users..."
                  value={userSearchTerm}
                  onChange={(e) => setUserSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-2">
              {filteredUsers.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  {availableUsers.length === 0
                    ? "Loading users..."
                    : "No users found"}
                </div>
              ) : (
                <div className="space-y-1">
                  {filteredUsers.map((u) => (
                    <button
                      key={u._id}
                      onClick={() => handleSwitchUser(u._id)}
                      className="w-full text-left px-3 py-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
                          {u.profileImage ? (
                            <img
                              src={u.profileImage}
                              alt="Profile"
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <User className="w-4 h-4 text-white" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                            {u.firstName} {u.lastName}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {u.email}
                          </p>
                        </div>
                        <span className="text-xs text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                          {u.role}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      <header
        className={`bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-2 ${user?.impersonating ? 'mt-[44px]' : ''}`}
      >
        <div className="flex items-center justify-between gap-4">
          {/* Mobile Hamburger + Logo */}
          <div className="flex items-center gap-3">
            {/* Hamburger Menu Button - Mobile only */}
            <button
              onClick={onMobileMenuToggle}
              className="md:hidden p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
              aria-label="Toggle menu"
            >
              <Menu className="w-6 h-6" />
            </button>

            {/* Debug: Impersonation indicator */}
            {user?.impersonating && (
              <div className="bg-red-500 text-white px-3 py-1 rounded text-xs font-bold">
                IMPERSONATING: {user.firstName} {user.lastName}
              </div>
            )}

            {/* Breadcrumb Navigation - Hidden on mobile */}
            <nav
              className="hidden md:flex items-center"
              aria-label="Breadcrumb"
            >
              <ol className="flex items-center space-x-2">
                {getBreadcrumbs().map((crumb, index, array) => {
                  const isLast = index === array.length - 1;
                  return (
                    <li
                      key={`${crumb.path}-${index}`}
                      className="flex items-center"
                    >
                      {index > 0 && (
                        <ChevronRight className="w-4 h-4 mx-2 text-gray-400 dark:text-gray-500" />
                      )}
                      {isLast ? (
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {crumb.label}
                        </span>
                      ) : (
                        <a
                          href={crumb.path}
                          className="text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-primary-500 dark:hover:text-primary-400 transition-colors flex items-center"
                        >
                          {crumb.icon === "Home" && (
                            <Home className="w-4 h-4" />
                          )}
                          {crumb.icon !== "Home" && <span>{crumb.label}</span>}
                        </a>
                      )}
                    </li>
                  );
                })}
              </ol>
            </nav>
          </div>

          {/* Global Search */}
          <GlobalSearch />

          {/* Right Side Controls - Desktop only */}
          <div className="hidden md:flex items-center space-x-4">
            {/* Token Balance Display */}
            <div className="relative">
              <button
                onClick={() => setShowTokenMenu(!showTokenMenu)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all shadow-sm hover:shadow-md ${
                  isLowBalance
                    ? "bg-warning-100 dark:bg-warning-900/30 text-warning-700 dark:text-warning-400 hover:bg-warning-200 dark:hover:bg-warning-900/50"
                    : "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 hover:bg-amber-200 dark:hover:bg-amber-900/50"
                }`}
                aria-label="Token balance"
                title="Token Balance"
              >
                <Coins className={`w-4 h-4 ${isLowBalance ? "text-warning-600 dark:text-warning-400" : "text-amber-600 dark:text-amber-400"}`} />
                <span className="text-sm font-semibold">
                  {tokenLoading ? "..." : totalTokens.toLocaleString()}
                </span>
              </button>

              {showTokenMenu && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setShowTokenMenu(false)}
                  />
                  <div className="absolute right-0 top-full mt-2 w-80 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50">
                    <div className="p-4">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                        Token Balance
                      </h3>
                      
                      <div className="space-y-3 mb-4">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600 dark:text-gray-400">Free Tokens (Monthly):</span>
                          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {freeTokens.toLocaleString()} / {freeMonthlyAllowance.toLocaleString()}
                          </span>
                        </div>
                        {purchasedTokensInBalance > 0 && (
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600 dark:text-gray-400">Purchased Tokens:</span>
                            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              {purchasedTokensInBalance.toLocaleString()}
                            </span>
                          </div>
                        )}
                        <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">Total Available:</span>
                            <span className="text-lg font-bold text-primary-600 dark:text-primary-400">
                              {totalTokens.toLocaleString()}
                            </span>
                          </div>
                        </div>
                      </div>

                      {isLowBalance && (
                        <div className="mb-3 p-2 bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-800 rounded text-xs text-warning-800 dark:text-warning-300">
                          Your token balance is running low
                        </div>
                      )}

                      <div className="space-y-2">
                        <button
                          onClick={() => {
                            setShowTokenMenu(false);
                            openPurchaseModal();
                          }}
                          className="w-full bg-primary-600 hover:bg-primary-700 text-white font-medium py-2 px-4 rounded-lg transition-colors text-sm"
                        >
                          Buy More Tokens
                        </button>
                        <button
                          onClick={() => {
                            setShowTokenMenu(false);
                            navigate('/admin?tab=account&subTab=usage');
                          }}
                          className="w-full bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 font-medium py-2 px-4 rounded-lg transition-colors text-sm"
                        >
                          View Usage & History
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* AI Architect Panel Button (Dev Mode Only) */}
            {isDevelopment && (
              <button
                onClick={() => setShowArchitectPanel(!showArchitectPanel)}
                className={`p-2 rounded-lg transition-colors ${
                  showArchitectPanel
                    ? "bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400"
                    : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
                }`}
                aria-label="AI Architect Panel"
                title="🏗️ AI Architect Panel (Dev Mode)"
              >
                <Brain className="w-6 h-6" />
              </button>
            )}

            {/* User Management Button (System Admin and Account Owner only) */}
            {(user?.role === "System Administrator" ||
              user?.accountRole === "Account Owner") && (
              <div className="relative">
                <button
                  onClick={() => setShowUserManagement(true)}
                  className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
                  aria-label={t("user.manageUsers")}
                >
                  <UserPlus className="w-6 h-6" />
                </button>
              </div>
            )}

            {/* Language Selector */}
            <div className="relative">
              <button
                onClick={() => {
                  console.log(
                    `[Header] Language button clicked. Current language: ${language}`,
                  );
                  setShowLanguageMenu(!showLanguageMenu);
                }}
                className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors flex items-center gap-1"
                aria-label="Language settings"
                title={`Current: ${language} - ${t("language.selectLanguage")}`}
              >
                <Languages className="w-6 h-6" />
                <span className="text-xs font-medium">
                  {language.toUpperCase()}
                </span>
              </button>

              {showLanguageMenu && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setShowLanguageMenu(false)}
                  />
                  <div className="absolute right-0 top-full mt-2 w-72 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 max-h-[500px] overflow-y-auto">
                    <div className="p-2">
                      <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide border-b border-gray-200 dark:border-gray-700 mb-2">
                        {t("language.selectLanguage")} ({language})
                      </div>
                      {Object.values(SUPPORTED_LANGUAGES).map((lang) => (
                        <button
                          key={lang.code}
                          onClick={() => {
                            changeLanguage(lang.code);
                            setShowLanguageMenu(false);
                          }}
                          className={`w-full text-left px-3 py-2 text-sm rounded flex items-center justify-between ${
                            language === lang.code
                              ? "bg-primary-50 dark:bg-gray-700 dark:border dark:border-primary-800/50 text-primary-700 dark:text-primary-300"
                              : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                          }`}
                        >
                          <div>
                            <div className="font-medium">{lang.nativeName}</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {lang.name}
                            </div>
                          </div>
                          {language === lang.code && (
                            <span className="text-primary-700 dark:text-primary-300">
                              ✓
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Theme Toggle */}
            <div className="relative">
              <button
                onClick={() => setShowThemeMenu(!showThemeMenu)}
                className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
                aria-label={t("theme.settings")}
              >
                {theme.mode === "light" ? (
                  <Sun className="w-6 h-6" />
                ) : theme.mode === "dark" ? (
                  <Moon className="w-6 h-6" />
                ) : (
                  <Monitor className="w-6 h-6" />
                )}
              </button>

              {showThemeMenu && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setShowThemeMenu(false)}
                  />
                  <div className="absolute right-0 top-full mt-2 w-72 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg z-50 max-h-[80vh] overflow-y-auto">
                    <div className="p-2">
                      <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                        {t("theme.theme")}
                      </div>
                      <button
                        onClick={() => handleThemeChange("light")}
                        className={`w-full text-left px-3 py-2 text-sm rounded ${theme.mode === "light" ? "bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300" : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"}`}
                      >
                        {t("theme.light")}
                      </button>
                      <button
                        onClick={() => handleThemeChange("dark")}
                        className={`w-full text-left px-3 py-2 text-sm rounded ${theme.mode === "dark" ? "bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300" : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"}`}
                      >
                        {t("theme.dark")}
                      </button>
                      <button
                        onClick={() => handleThemeChange("system")}
                        className={`w-full text-left px-3 py-2 text-sm rounded ${theme.mode === "system" ? "bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300" : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"}`}
                      >
                        {t("theme.system")}
                      </button>
                    </div>
                    <div className="border-t border-gray-200 dark:border-gray-600 p-2">
                      <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                        {t("theme.viewMode")}
                      </div>
                      <button
                        onClick={() => handleViewModeChange("standard")}
                        className={`w-full text-left px-3 py-2 text-sm rounded ${theme.viewMode === "standard" ? "bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300" : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"}`}
                      >
                        {t("theme.standard")}
                      </button>
                      <button
                        onClick={() => handleViewModeChange("compact")}
                        className={`w-full text-left px-3 py-2 text-sm rounded ${theme.viewMode === "compact" ? "bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300" : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"}`}
                      >
                        {t("theme.compact")}
                      </button>
                    </div>
                    <div className="border-t border-gray-200 dark:border-gray-600 p-2">
                      <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                        {t("theme.accessibility")}
                      </div>

                      {/* Font Size */}
                      <div className="px-3 py-2">
                        <div className="flex items-center space-x-2 mb-1">
                          <Type className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                          <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                            {t("theme.fontSize")}
                          </label>
                        </div>
                        <select
                          value={accessibility.fontSize}
                          onChange={(e) =>
                            setAccessibility({
                              ...accessibility,
                              fontSize: e.target.value as any,
                            })
                          }
                          className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-800 dark:text-gray-100"
                        >
                          <option value="small">{t("theme.small")}</option>
                          <option value="normal">{t("theme.normal")}</option>
                          <option value="large">{t("theme.large")}</option>
                          <option value="extra-large">
                            {t("theme.extraLarge")}
                          </option>
                        </select>
                      </div>

                      {/* High Contrast */}
                      <div className="flex items-center justify-between px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                        <div className="flex items-center space-x-2">
                          <Palette className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                          <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                            {t("theme.highContrast")}
                          </label>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={accessibility.highContrast}
                            onChange={(e) =>
                              setAccessibility({
                                ...accessibility,
                                highContrast: e.target.checked,
                              })
                            }
                            className="sr-only peer"
                          />
                          <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600"></div>
                        </label>
                      </div>

                      {/* Reduced Motion */}
                      <div className="flex items-center justify-between px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                        <div className="flex items-center space-x-2">
                          <Zap className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                          <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                            {t("theme.reducedMotion")}
                          </label>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={accessibility.reducedMotion}
                            onChange={(e) =>
                              setAccessibility({
                                ...accessibility,
                                reducedMotion: e.target.checked,
                              })
                            }
                            className="sr-only peer"
                          />
                          <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600"></div>
                        </label>
                      </div>

                      {/* Color Blind Support */}
                      <div className="flex items-center justify-between px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                        <div className="flex items-center space-x-2">
                          <Eye className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                          <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                            {t("theme.colorBlindSupport")}
                          </label>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={accessibility.colorBlindSupport}
                            onChange={(e) =>
                              setAccessibility({
                                ...accessibility,
                                colorBlindSupport: e.target.checked,
                              })
                            }
                            className="sr-only peer"
                          />
                          <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600"></div>
                        </label>
                      </div>

                      {/* Screen Reader Optimized */}
                      <div className="flex items-center justify-between px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                        <div className="flex items-center space-x-2">
                          <Volume2 className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                          <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                            {t("theme.screenReader")}
                          </label>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={accessibility.screenReaderOptimized}
                            onChange={(e) =>
                              setAccessibility({
                                ...accessibility,
                                screenReaderOptimized: e.target.checked,
                              })
                            }
                            className="sr-only peer"
                          />
                          <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600"></div>
                        </label>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* User Profile */}
            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
                aria-label="User menu"
              >
                <div className="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center overflow-hidden">
                  {(user as any)?.profileImage ? (
                    <img
                      src={(user as any).profileImage}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-sm font-semibold text-white">
                      {getUserInitials(user?.firstName, user?.lastName)}
                    </span>
                  )}
                </div>
              </button>

              {showUserMenu && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setShowUserMenu(false)}
                  />
                  <div className="absolute right-0 top-full mt-2 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50">
                    <div className="p-2">
                      <button
                        onClick={handleProfileClick}
                        className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded flex items-center space-x-2"
                      >
                        <User className="w-4 h-4" />
                        <span>{t("navigation.profile")}</span>
                      </button>
                      <hr className="my-1 border-gray-200 dark:border-gray-700" />
                      <button
                        onClick={logout}
                        className="w-full text-left px-3 py-2 text-sm text-error-600 dark:text-error-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded flex items-center space-x-2"
                      >
                        <LogOut className="w-4 h-4" />
                        <span>{t("auth.logout")}</span>
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Mobile User Profile Button - Simple Icon Only */}
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            className="md:hidden p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
            aria-label="User menu"
          >
            <div className="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center overflow-hidden">
              {(user as any)?.profileImage ? (
                <img
                  src={(user as any).profileImage}
                  alt="Profile"
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-sm font-semibold text-white">
                  {getUserInitials(user?.firstName, user?.lastName)}
                </span>
              )}
            </div>
          </button>

          {/* Mobile User Menu - Full Screen Modal */}
          {showUserMenu && (
            <div
              className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-50"
              onClick={() => setShowUserMenu(false)}
            >
              <div
                className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 rounded-t-2xl shadow-lg"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6">
                  {/* User Info */}
                  <div className="flex items-center space-x-3 mb-6 pb-4 border-b border-gray-200 dark:border-gray-700">
                    <div className="w-12 h-12 bg-primary-500 rounded-full flex items-center justify-center">
                      <User className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {user?.email || "User"}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {user?.role || "User"}
                      </p>
                    </div>
                  </div>

                  {/* Theme Selection */}
                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                      {t("theme.theme")}
                    </h3>
                    <div className="grid grid-cols-3 gap-2">
                      <button
                        onClick={() => handleThemeChange("light")}
                        className={`flex flex-col items-center p-3 rounded-lg border ${
                          theme.mode === "light"
                            ? "bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 border-primary-200 dark:border-primary-800/50"
                            : "border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300"
                        }`}
                      >
                        <Sun className="w-5 h-5 mb-1" />
                        <span className="text-xs">{t("theme.light")}</span>
                      </button>
                      <button
                        onClick={() => handleThemeChange("dark")}
                        className={`flex flex-col items-center p-3 rounded-lg border ${
                          theme.mode === "dark"
                            ? "bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 border-primary-200 dark:border-primary-800/50"
                            : "border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300"
                        }`}
                      >
                        <Moon className="w-5 h-5 mb-1" />
                        <span className="text-xs">{t("theme.dark")}</span>
                      </button>
                      <button
                        onClick={() => handleThemeChange("system")}
                        className={`flex flex-col items-center p-3 rounded-lg border ${
                          theme.mode === "system"
                            ? "bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 border-primary-200 dark:border-primary-800/50"
                            : "border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300"
                        }`}
                      >
                        <Monitor className="w-5 h-5 mb-1" />
                        <span className="text-xs">{t("theme.system")}</span>
                      </button>
                    </div>
                  </div>

                  {/* Language Selection */}
                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                      {t("language.selectLanguage")}
                    </h3>
                    <div className="max-h-48 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-600">
                      {Object.values(SUPPORTED_LANGUAGES).map((lang) => (
                        <button
                          key={lang.code}
                          onClick={() => {
                            changeLanguage(lang.code);
                          }}
                          className={`w-full text-left px-3 py-2 flex items-center justify-between border-b border-gray-100 dark:border-gray-700 last:border-0 ${
                            language === lang.code
                              ? "bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300"
                              : "hover:bg-gray-50 dark:hover:bg-gray-700"
                          }`}
                        >
                          <div>
                            <div className="text-sm font-medium">
                              {lang.nativeName}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {lang.name}
                            </div>
                          </div>
                          {language === lang.code && (
                            <span className="text-primary-700 dark:text-primary-300">
                              ✓
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Quick Accessibility Toggles */}
                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                      {t("theme.quickSettings")}
                    </h3>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          {t("theme.highContrast")}
                        </span>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={accessibility.highContrast}
                            onChange={(e) =>
                              setAccessibility({
                                ...accessibility,
                                highContrast: e.target.checked,
                              })
                            }
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-300 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600"></div>
                        </label>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          {t("theme.reducedMotion")}
                        </span>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={accessibility.reducedMotion}
                            onChange={(e) =>
                              setAccessibility({
                                ...accessibility,
                                reducedMotion: e.target.checked,
                              })
                            }
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-300 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600"></div>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="space-y-2">
                    <button
                      onClick={handleAdminClick}
                      className="w-full text-left px-4 py-3 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg flex items-center space-x-3"
                    >
                      <Settings className="w-5 h-5" />
                      <span>Admin</span>
                    </button>
                    <button
                      onClick={() => {
                        logout();
                        setShowUserMenu(false);
                      }}
                      className="w-full text-left px-4 py-3 text-error-600 dark:text-error-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg flex items-center space-x-3"
                    >
                      <LogOut className="w-5 h-5" />
                      <span>Sign Out</span>
                    </button>
                  </div>

                  {/* Close Button */}
                  <button
                    onClick={() => setShowUserMenu(false)}
                    className="w-full mt-4 py-3 text-center text-gray-600 dark:text-gray-400 font-medium"
                  >
                    {t("common.close")}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* User Management Modal */}
        {showUserManagement && (
          <UserManagement onClose={() => setShowUserManagement(false)} />
        )}

        {/* Profile Modal */}
        {showProfileModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              <div className="flex-shrink-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                  {t("navigation.profile")}
                </h2>
                <button
                  onClick={() => setShowProfileModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-6">
                <AdminProfile />
              </div>
            </div>
          </div>
        )}

        {/* AI Architect Panel (Dev Mode Only) */}
        {isDevelopment && showArchitectPanel && (
          <AIArchitectPanel
            isOpen={showArchitectPanel}
            onClose={() => {
              setShowArchitectPanel(false);
              setArchitectTrigger(null);
            }}
            trigger={architectTrigger}
          />
        )}
      </header>
    </>
  );
};
